import { Alert, AlertDescription, AlertIcon, Checkbox, Input, Stack, Text } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { FormField } from '../../../components/FormField'
import { FormModal } from '../../../components/FormModal'
import { formatApiDate, nowSaoPauloDateTimeLocal, useAppToast } from '../../../helpers'
import { EmployeeService } from '../../../services/Employee/EmployeeService'
import { IEmployeeDetailRaw, ILegacyMatchRaw, ISameEmailRaw } from '../../../services/Employee/types'
import { EMPLOYEE_STATUS, MAGENTA_LABELS } from './employeeConstants'

export type TEmployeeAction = 'approve' | 'inactivate' | 'reactivate'

interface IEmployeeActionModalProps {
  action: TEmployeeAction | null
  detail: IEmployeeDetailRaw | null
  onClose: () => void
  onDone: (updated: IEmployeeDetailRaw) => void
}

// `done` builds the toast, worded on the act (admission, termination) so it reads the same for anyone.
const ACTION_TEXT: Record<
  TEmployeeAction,
  { title: string; label: string; confirm: string; done: (name: string | null) => string }
> = {
  approve: {
    title: 'Aprovar',
    label: 'Data de Admissão',
    confirm: 'Aprovar',
    done: (name) => `Admissão de ${name ?? 'colaborador'} aprovada`
  },
  inactivate: {
    title: 'Inativar',
    label: 'Data de Desligamento',
    confirm: 'Inativar',
    done: (name) => `Desligamento de ${name ?? 'colaborador'} registrado`
  },
  reactivate: {
    title: 'Reativar',
    label: 'Nova Data de Admissão',
    confirm: 'Reativar',
    done: (name) => `Readmissão de ${name ?? 'colaborador'} registrada`
  }
}

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`

/** What a legacy record would bring: "3 avaliações (2 Acelera, 1 Conexão)". */
const evaluationsSummary = (evaluations: ILegacyMatchRaw['evaluations']) => {
  const keys = (Object.keys(evaluations) as (keyof typeof evaluations)[]).filter((key) => evaluations[key] > 0)
  const total = keys.reduce((sum, key) => sum + evaluations[key], 0)
  const parts = keys.map((key) => `${evaluations[key]} ${MAGENTA_LABELS[key]}`)

  return `${plural(total, 'avaliação', 'avaliações')}${parts.length ? ` (${parts.join(', ')})` : ''}`
}

/**
 * The legacy records with the same e-mail, one checkbox each and none checked: different people
 * sometimes shared an e-mail in the Magentas, so RH picks the ones of this person.
 */
export function LegacyLinkField({
  matches,
  selected,
  onChange
}: {
  matches: ILegacyMatchRaw[]
  selected: number[]
  onChange: (ids: number[]) => void
}) {
  if (matches.length === 0) return null

  const toggle = (id: number, checked: boolean) =>
    onChange(checked ? [...selected, id] : selected.filter((other) => other !== id))

  return (
    <FormField
      label="Registros dos Magentas com este e-mail"
      helperText="Marque só os desta pessoa. As avaliações passam para o colaborador, guardando os dados da época, e os registros marcados são apagados."
    >
      <Stack spacing={2}>
        {matches.map((match) => (
          <Checkbox
            key={match.id}
            isChecked={selected.includes(match.id)}
            onChange={(e) => toggle(match.id, e.target.checked)}
            alignItems="flex-start"
          >
            <Text fontSize="sm">{match.name ?? 'Sem nome'}</Text>
            <Text variant="caption">
              {[
                match.position,
                match.created_at && `criado em ${formatApiDate(match.created_at)}`,
                evaluationsSummary(match.evaluations)
              ]
                .filter(Boolean)
                .join(' · ')}
            </Text>
          </Checkbox>
        ))}
      </Stack>
    </FormField>
  )
}

/** Other collaborators with the same e-mail: allowed, but RH should know. */
export function SameEmailWarning({ people }: { people: ISameEmailRaw[] }) {
  if (people.length === 0) return null

  return (
    <Alert status="warning" borderRadius="md">
      <AlertIcon />
      <AlertDescription fontSize="sm">
        Já existe colaborador com este e-mail:{' '}
        {people.map((person) => `${person.name ?? 'Sem nome'} (${EMPLOYEE_STATUS[person.status].label})`).join(', ')}.
      </AlertDescription>
    </Alert>
  )
}

/**
 * Approve (admission date and the legacy link), inactivate (termination date) and reactivate
 * (new admission date). The API's date rules are checked here too, with its messages.
 */
export function EmployeeActionModal({ action, detail, onClose, onDone }: IEmployeeActionModalProps) {
  const toast = useAppToast()
  // The API validates against São Paulo's date, whatever the browser's time zone.
  const today = nowSaoPauloDateTimeLocal().slice(0, 10)
  const [date, setDate] = useState(today)
  const [legacyIds, setLegacyIds] = useState<number[]>([])
  const [error, setError] = useState<string[]>()
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!action) return
    setDate(today)
    setLegacyIds([])
    setError(undefined)
  }, [action])

  const text = action ? ACTION_TEXT[action] : null

  const clientError = (): string | null => {
    if (!detail || !action) return null
    if (action === 'inactivate') {
      if (detail.admission_date && date < detail.admission_date) return 'O desligamento não pode ser antes da admissão.'
      if (date > today) return 'O desligamento não pode ser futuro.'
    }
    if (action === 'reactivate' && detail.termination_date && date <= detail.termination_date) {
      return 'A nova admissão precisa ser depois do último desligamento.'
    }
    return null
  }

  const handleSave = () => {
    if (!detail || !action || !text) return

    const message = clientError()
    if (message) {
      setError([message])
      return
    }

    const service = new EmployeeService()
    const request =
      action === 'approve'
        ? service.approve(detail.id, date, legacyIds)
        : action === 'inactivate'
          ? service.inactivate(detail.id, date)
          : service.reactivate(detail.id, date)

    setSaving(true)
    request
      .then((updated) => {
        toast.success(text.done(updated.name))
        onDone(updated)
        onClose()
      })
      .catch((err) => {
        const errors = err?.response?.data?.errors
        const field = action === 'inactivate' ? 'termination_date' : 'admission_date'
        if (errors?.[field]) {
          setError(errors[field])
          return
        }
        toast.error(`Erro ao ${text.title.toLowerCase()}`, err)
      })
      .finally(() => setSaving(false))
  }

  const helper =
    action === 'inactivate'
      ? detail?.admission_date
        ? `Admitido em ${formatApiDate(detail.admission_date)}.`
        : undefined
      : action === 'reactivate' && detail?.termination_date
        ? `Desligado em ${formatApiDate(detail.termination_date)}.`
        : undefined

  return (
    <FormModal
      isOpen={action !== null}
      onClose={onClose}
      title={text && detail ? `${text.title} ${detail.name ?? ''}` : ''}
      onSubmit={handleSave}
      size="default"
      confirmLabel={text?.confirm}
      confirmAction={action === 'approve' ? 'approve' : undefined}
      isLoading={saving}
      isConfirmDisabled={!date}
      // The error goes under the field, like the rest of the panel, not in the browser's bubble.
      noValidate
    >
      <Stack spacing={4}>
        <FormField label={text?.label} error={error} helperText={helper} isRequired>
          <Input
            type="date"
            value={date}
            min={action === 'inactivate' ? (detail?.admission_date ?? undefined) : undefined}
            max={action === 'inactivate' ? today : undefined}
            onChange={(e) => {
              setDate(e.target.value)
              setError(undefined)
            }}
            isDisabled={saving}
          />
        </FormField>

        {action === 'approve' && detail && (
          <>
            <LegacyLinkField matches={detail.legacy_matches} selected={legacyIds} onChange={setLegacyIds} />
            <SameEmailWarning people={detail.same_email} />
          </>
        )}
      </Stack>
    </FormModal>
  )
}
